/**
 * apiClient (Phase 2 Task 2) — /api/* 호출 wrapper
 *
 * 기능:
 *  1. Authorization: Bearer {credential} 자동 부착 (localStorage 안 mediaplex_auth_credential)
 *  2. 401 응답 시 refreshToken 시도 → 성공이면 1회 재시도 / 실패면 logout
 *  3. 동시 다발 401 발생 시 큐잉 — 중복 refresh 방지
 *  4. credential 부재(예: DEV bypass) 시 401은 호출자에게 그대로 반환 (재시도/logout skip)
 *
 * refresh/logout 핸들러는 AuthContext가 mount 시 wire (setRefreshHandler / setLogoutHandler).
 * 기본값은 의도적으로 throw — AuthContext 미마운트 시 logout 흐름 트리거.
 */

const LS_KEY = "mediaplex_auth_credential";
export const AUTH_OPERATION_STALE = "AUTH_OPERATION_STALE";

let isRefreshing = false;
let appSessionActive = false;
let sessionPrincipal = "";
let sessionEpoch = 0;
export function setSessionPrincipal(email) {
  const next = typeof email === "string" ? email.trim().toLowerCase() : "";
  if (next !== sessionPrincipal) { sessionPrincipal = next; sessionEpoch++; }
}
const staleSession = () => Object.assign(new Error(AUTH_OPERATION_STALE), { code: AUTH_OPERATION_STALE });
export function setAppSessionActive(active) { appSessionActive = active === true; }
/** @type {Array<{ resolve: () => void, reject: (err: Error) => void }>} */
let failedQueue = [];

function processQueue(error) {
  for (const { resolve, reject } of failedQueue) {
    if (error) reject(error);
    else resolve();
  }
  failedQueue = [];
}

// 기본 refresh: AuthContext에서 wire 안 한 경우 실패 → logout
const defaultRefreshHandler = async () => {
  throw new Error("REFRESH_HANDLER_NOT_CONFIGURED");
};
const defaultLogoutHandler = () => {
  try {
    localStorage.removeItem(LS_KEY);
  } catch {
    /* SSR/사파리 사생활 모드 등 */
  }
  if (typeof window !== "undefined" && window.location) {
    window.location.reload();
  }
};
let refreshHandler = defaultRefreshHandler;
let logoutHandler = defaultLogoutHandler;

export function setRefreshHandler(fn) {
  if (typeof fn !== "function") return () => {};
  refreshHandler = fn;
  return () => {
    if (refreshHandler === fn) refreshHandler = defaultRefreshHandler;
  };
}
export function setLogoutHandler(fn) {
  if (typeof fn !== "function") return () => {};
  logoutHandler = fn;
  return () => {
    if (logoutHandler === fn) logoutHandler = defaultLogoutHandler;
  };
}

/**
 * 인증 헤더 + 401 interceptor 부착된 fetch.
 *
 * @param {string} url
 * @param {RequestInit} [options]
 * @returns {Promise<Response>}
 */
export async function apiClient(url, options = {}, _retryCount = 0) {
  const requestEpoch = sessionEpoch;
  let credential = "";
  try {
    credential = localStorage.getItem(LS_KEY) || "";
  } catch {
    /* SSR 또는 localStorage 차단 환경 */
  }

  const headers = new Headers(options.headers || {});
  const supplied = headers.get("Authorization");
  const followsSession = !supplied || /^Bearer\s*$/i.test(supplied) || supplied === `Bearer ${credential}`;
  let retryOptions = options;
  if (followsSession) {
    headers.delete("Authorization");
    if (credential) headers.set("Authorization", `Bearer ${credential}`);
    // A caller may have copied the old session token into options. Rebuild it after refresh.
    const retryHeaders = new Headers(options.headers || {});
    retryHeaders.delete("Authorization");
    retryOptions = { ...options, headers: retryHeaders };
  }

  const response = await fetch(url, { ...options, headers });

  // credential 없으면 (DEV bypass 등) refresh/logout skip — 401 그대로 반환
  if (!credential && !appSessionActive) return response;

  if (response.status !== 401) return response;
  if (!followsSession) return response;
  if (requestEpoch !== sessionEpoch) throw staleSession();

  // 한 번 재시도했는데도 401 → 진짜 만료/거부 → logout
  if (_retryCount >= 1) {
    logoutHandler();
    return response;
  }

  // 첫 번째 401: refresh 시도 (중복 방지 큐잉)
  if (isRefreshing) {
    try {
      await new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      });
    } catch (err) {
      // refresh 실패가 큐에 전파됨 — 호출자에게도 그대로 throw
      throw err;
    }
    if (requestEpoch !== sessionEpoch) throw staleSession();
    return apiClient(url, retryOptions, _retryCount + 1);
  }

  isRefreshing = true;
  try {
    await refreshHandler();
    processQueue(null);
    if (requestEpoch !== sessionEpoch) throw staleSession();
    return apiClient(url, retryOptions, _retryCount + 1);
  } catch (err) {
    processQueue(err);
    // logout/새 login으로 무효화된 이전 refresh 실패는 현재 세션을 만료시키지 않는다.
    if (requestEpoch === sessionEpoch && err?.code !== AUTH_OPERATION_STALE && err?.code !== "AUTH_SERVICE_UNAVAILABLE") logoutHandler();
    throw err;
  } finally {
    isRefreshing = false;
  }
}
